import { Info, Bell } from 'lucide-react';
import { User as AuthUser } from '@/types/user';
import { CoinIcon, SokoCoinIcon } from "@/app/components/SvgIcons";
import { memo, useCallback, useState, useRef, useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import api from "@/app/lib/axios";
import { PointsLog } from "@/types/pointsLog";
import { formatDate } from "@/Utils/FormatDate";
import { getPointsLogCurrency } from "@/Utils/pointsLog";
import { Clock } from 'lucide-react';
import AgeVerificationModal from '@/app/components/AgeVerificationModal';
import Avatar from '@/app/components/Avatar';
import KickIcon from '@/app/components/Navbar/KickIcon';
import { useKickLogin } from '@/app/hooks/useKickLogin';

interface AuthButtonsProps {
    user: AuthUser | null;
    setShowProfileModal: React.Dispatch<React.SetStateAction<boolean>>;
}

interface PointsHistoryResponse {
    data: PointsLog[];
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
}

// Memoized Login Buttons Component
const LoginButtons = memo(() => {
    const [showAgeModal, setShowAgeModal] = useState(false);

    const doKickLogin = useKickLogin();

    return (
        <>
            <button
                onClick={() => setShowAgeModal(true)}
                className="custom-kick-button flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors"
                aria-label="Login with Kick"
            >
                <KickIcon />
            </button>
            {showAgeModal && (
                <AgeVerificationModal
                    onConfirm={() => { setShowAgeModal(false); doKickLogin(); }}
                    onClose={() => setShowAgeModal(false)}
                />
            )}
        </>
    );
});
LoginButtons.displayName = 'LoginButtons';

// Memoized Points Display Component
const PointsDisplay = memo(({
    points,
    onInfoClick
}: {
    points: number;
    onInfoClick: () => void;
}) => (
    <div className="flex items-center gap-2">
        <button onClick={onInfoClick} aria-label="Wallet information" className="rounded-xl p-2 hover:bg-gray-800/50 transition-colors">
            <Info size={18} className="text-gray-200" />
        </button>
        <div className="text-gray-200 flex items-center gap-1.5 px-6 py-2 bg-gray-900 rounded-xl">
            <span className="font-semibold">Kick Points {points.toLocaleString()}</span>
            <CoinIcon size={15} />
        </div>
    </div>
));
PointsDisplay.displayName = 'PointsDisplay';

// Notifications Dropdown Component
function NotificationsDropdown({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    const dropdownRef = useRef<HTMLDivElement>(null);

    const { data, isLoading, isError } = useQuery<PointsHistoryResponse>({
        queryKey: ['pointsHistoryRecent'],
        queryFn: async () => {
            const response = await api.get("points", {
                params: { page: 1, per_page: 10 }
            });
            return response.data;
        },
        staleTime: 0,
        enabled: isOpen,
    });

    if (!isOpen) return null;

    return (
        <div
            ref={dropdownRef}
            className="absolute right-[-60px] top-15 md:right-0 md:top-full mt-2 w-[calc(100vw-2rem)] sm:w-96 bg-gray-900 rounded-lg shadow-xl z-50 max-h-[53vh] sm:max-h-[500px] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
        >
            {/* Header */}
            <div className="p-3 sm:p-4">
                <h3 className="text-white font-semibold text-base">Notifications</h3>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1 scrollbar-hide">
                {isLoading && (
                    <div className="text-center py-8 text-gray-400">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400 mx-auto mb-2"></div>
                        <p className="text-sm">Loading...</p>
                    </div>
                )}

                {isError && (
                    <div className="text-center py-8 text-red-400">
                        <p className="text-sm">Failed to load transactions</p>
                    </div>
                )}

                {!isLoading && !isError && data && data.data.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        <Bell size={32} className="mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No transactions yet</p>
                    </div>
                )}

                {!isLoading && !isError && data && data.data.length > 0 && (
                    <div className="text-left">
                        {data.data.slice(0, 10).map(item => (
                            <div key={item.id} className="p-3 sm:p-4 hover:bg-gray-800/50 transition-colors">
                                <div className="flex items-center justify-between mb-1 gap-2">
                                    <div className="font-medium text-white text-sm truncate flex-1">{item.event_name}</div>
                                    <div className={`flex items-center gap-1 font-bold text-sm whitespace-nowrap ${item.type === 'increment' ? 'text-green-400' : 'text-red-400'
                                        }`}>
                                        <span>{item.type === 'increment' ? '+' : '-'}{item.points.toLocaleString()}</span>
                                        {getPointsLogCurrency(item) === 'soko_points'
                                            ? <SokoCoinIcon size={12} />
                                            : <CoinIcon size={12} />}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-400">
                                    <Clock size={10} className="sm:w-3 sm:h-3" />
                                    {formatDate(item.created_at)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
// Memoized Notifications Button Component
const NotificationsButton = memo(({
    onClick,
    hasNotifications
}: {
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    hasNotifications?: boolean
}) => (
    <button
        onClick={onClick}
        className="relative text-gray-200 flex items-center p-2 rounded-xl hover:bg-gray-800 transition-colors"
        aria-label="View notifications"
    >
        <Bell size={20} />
        {hasNotifications && (
            <span className="absolute top-2 right-2 w-2 h-2 bg-green-400 rounded-full"></span>
        )}
    </button>
));
NotificationsButton.displayName = 'NotificationsButton';

// Memoized Username Bubble Component
const UsernameBubble = memo(({
    username,
    avatar,
    onClick
}: {
    username: string;
    avatar?: string | null;
    onClick: () => void
}) => (
    <button
        onClick={onClick}
        className="flex items-center gap-3 py-2"
        aria-label="Open profile"
    >
        <Avatar
            src={avatar}
            username={username}
            className="w-12 h-12 rounded-full border-2 border-gray-600 hover:border-gray-500 transition-all"
            fallbackClassName="bg-gray-800 text-base hover:bg-gray-700"
        />
    </button>
));
UsernameBubble.displayName = 'UsernameBubble';

// Main Component (Not memoized because props change frequently)
export default function AuthButtons({
    user,
    setShowProfileModal,
}: AuthButtonsProps) {
    const [showNotifications, setShowNotifications] = useState(false);
    const notificationButtonRef = useRef<HTMLDivElement>(null);

    // Memoized handlers
    const handleShowProfile = useCallback(() => {
        setShowProfileModal(true);
    }, [setShowProfileModal]);

    const handleToggleNotifications = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setShowNotifications(prev => !prev);
    }, []);

    const handleCloseNotifications = useCallback(() => {
        setShowNotifications(false);
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (notificationButtonRef.current && !notificationButtonRef.current.contains(event.target as Node)) {
                setShowNotifications(false);
            }
        }

        if (showNotifications) {
            // Use setTimeout to avoid immediate closure
            setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 0);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showNotifications]);

    // Not logged in
    if (!user) {
        return <LoginButtons />;
    }

    const isKickUser = user.provider === 'kick';

    return (
        <div className="flex items-center gap-3">
            {isKickUser && (
                <div className="relative" ref={notificationButtonRef}>
                    <NotificationsButton
                        onClick={handleToggleNotifications}
                    />
                    <NotificationsDropdown
                        isOpen={showNotifications}
                        onClose={handleCloseNotifications}
                    />
                </div>
            )}

            <UsernameBubble
                username={user.username}
                avatar={user.avatar}
                onClick={handleShowProfile}
            />
        </div>
    );
}